import React, { memo, useCallback, useMemo, useState } from "react";

import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Divider,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DoubleCircleArrowsIcon } from "modules/icons";
import colors from "app/colors";
import useSearchFilter from "modules/hooks/use-search-filter";
import { MessageThreadType, SearchFilter, SearchFilterType } from "gql/graphql";
import { SEARCH_PATH } from "modules/utils/Constants";
import FiltersDrawerThreadTypeButton from "modules/messaging/search/filters-drawer-thread-type-button";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import ThreadFilters from "./thread-filters";
import AllThreadsFilters from "./all-threads-filters";
import ChatFilters from "./chat-filters";
import TaskFilters from "./task-filters";

interface IProps {
  type?: MessageThreadType;
}

const FiltersDrawer = ({ type }: IProps) => {
  const { closeDrawer } = useOverlay();
  const { searchFilters, setSearchFilters } = useSearchFilter();
  const { track } = useMixpanel();

  const [newSearchFilters, setNewSearchFilters] = useState<SearchFilter[]>(
    type
      ? [
          { type: SearchFilterType.TypeList, stringListValue: [type] },
          ...searchFilters,
        ]
      : [...searchFilters],
  );

  const messageThreadType = useMemo(() => {
    if (type) return type;
    const messageThreadTypes =
      newSearchFilters.find(
        (filter) => filter.type === SearchFilterType.TypeList,
      )?.stringListValue ?? ([] as MessageThreadType[]);
    if (messageThreadTypes.length > 0) {
      return messageThreadTypes[0];
    }
  }, [newSearchFilters, type]);

  const title = useMemo(() => {
    switch (messageThreadType) {
      case MessageThreadType.Thread: {
        return "Thread Filters";
      }
      case MessageThreadType.Chat: {
        return "Chat Filters";
      }
      case MessageThreadType.Task: {
        return "Task Filters";
      }
      default: {
        return "Filters";
      }
    }
  }, [messageThreadType]);

  const onResetClick = useCallback(() => {
    setNewSearchFilters([]);
  }, []);

  const onApplyFiltersClick = useCallback(() => {
    setSearchFilters(newSearchFilters, SEARCH_PATH);
    track(MIXPANEL_EVENT_TYPE.FILTERED_CONVERSATIONS);
    closeDrawer();
  }, [setSearchFilters, closeDrawer, newSearchFilters, track]);

  const hasSearchFilters = newSearchFilters.length > 0;

  return (
    <DrawerContent>
      <DrawerHeader px={3} pb={0}>
        <HStack>
          <DrawerCloseButton size="lg" position="unset" />
          <Text fontWeight="bold" flex={1} textAlign="center">
            {title}
          </Text>
          <Button
            variant="outline"
            leftIcon={
              <DoubleCircleArrowsIcon boxSize={4} fill={colors.gray80} />
            }
            iconSpacing={1}
            size="xs"
            color={colors.gray80}
            onClick={onResetClick}
            borderRadius={4}
            borderColor={colors.gray80}
            isDisabled={!hasSearchFilters}
            px={1}
          >
            Reset
          </Button>
        </HStack>
        <Divider mt={3} />
      </DrawerHeader>

      <DrawerBody>
        <VStack w="full" spacing={6} divider={<Divider />}>
          {!type && (
            <FiltersDrawerThreadTypeButton
              newSearchFilters={newSearchFilters}
              setNewSearchFilters={setNewSearchFilters}
            />
          )}
          {!messageThreadType && (
            <AllThreadsFilters
              newSearchFilters={newSearchFilters}
              setNewSearchFilters={setNewSearchFilters}
            />
          )}
          {messageThreadType === MessageThreadType.Thread && (
            <ThreadFilters
              newSearchFilters={newSearchFilters}
              setNewSearchFilters={setNewSearchFilters}
            />
          )}
          {messageThreadType === MessageThreadType.Chat && (
            <ChatFilters
              newSearchFilters={newSearchFilters}
              setNewSearchFilters={setNewSearchFilters}
            />
          )}
          {messageThreadType === MessageThreadType.Task && (
            <TaskFilters
              newSearchFilters={newSearchFilters}
              setNewSearchFilters={setNewSearchFilters}
            />
          )}
        </VStack>
      </DrawerBody>

      <DrawerFooter boxShadow="0px -4px 4px rgba(0, 0, 0, 0.05)">
        <HStack justify="center" w="full">
          <HStack spacing={8}>
            <Button
              variant="outline"
              onClick={closeDrawer}
              w={140}
              borderColor={colors.gray40}
              color={colors.gray40}
              fontSize={16}
              fontWeight={700}
            >
              Cancel
            </Button>
            <Button
              variant="hover"
              onClick={onApplyFiltersClick}
              w={140}
              bg={colors.darkBlue}
              color={colors.white}
              fontSize={16}
              fontWeight={700}
            >
              Apply Filters
            </Button>
          </HStack>
        </HStack>
      </DrawerFooter>
    </DrawerContent>
  );
};

export default memo(FiltersDrawer);
